import type { KeyboardEvent, MouseEvent } from 'react';

import type { CvtConcernOption } from '../cvt-primary-concerns-modal.types';

import { colors } from '@packages/core/styles';
import { Typography } from '@packages/ui/shared';
import { chevron } from '@web/jss/mixins';
import classNames from 'classnames';
import { useId, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';

import { CvtConcernsMultiSelectChip } from './cvt-concerns-multi-select-chip';
import { CvtConcernsMultiSelectOption } from './cvt-concerns-multi-select-option';

const useStyles = createUseStyles({
    combobox: {
        alignItems: 'center',
        display: 'flex',
        flex: 1,
        minHeight: 30,
        minWidth: 60,
        outline: 'none',
    },
    field: {
        ...chevron({ color: colors.grayFive, leftOffset: 'auto', rightOffset: 12, topOffest: 20 }),
        alignItems: 'center',
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayFive}`,
        borderRadius: 3,
        cursor: 'pointer',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        minHeight: 40,
        padding: '4px 40px 4px 8px',
        position: 'relative',
        '&$focusedField': {
            borderColor: colors.blueOne,
        },
        '&:hover': {
            boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.16)',
        },
    },
    focusedField: {},
    menu: {
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 3,
        boxShadow: '0 8px 12px 0 rgba(0, 0, 0, 0.16)',
        left: 0,
        marginTop: 2,
        padding: '4px 0',
        position: 'absolute',
        right: 0,
        top: '100%',
        zIndex: 1000,
    },
    multiSelect: {
        position: 'relative',
    },
});

export type CvtConcernsMultiSelectProps = {
    /** The name screen readers give the combobox and the menu. */
    ariaLabel: string;

    /** Called with the selected options whenever one is added or removed. */
    onChange: (selectedOptions: CvtConcernOption[]) => void;

    /** Every option, in the order the menu lists them. */
    options: CvtConcernOption[];

    /** The text shown while nothing is selected and the menu is closed. */
    placeholder?: string;

    /** The text shown while nothing is selected and the menu is open. */
    placeholderWhileMenuOpen?: string;

    /** The selected options, shown as chips in this order. */
    selectedOptions: CvtConcernOption[];
};

/**
 * A multi-select for the CVT concerns, whose menu lists every option with a checkbox. Nothing can be typed in it.
 *
 * Renders the selected options as chips in the field, each with a button that removes it, and the menu below the field.
 */
export function CvtConcernsMultiSelect({
    ariaLabel,
    onChange,
    options,
    placeholder,
    placeholderWhileMenuOpen,
    selectedOptions,
}: CvtConcernsMultiSelectProps) {
    const classes = useStyles();
    const comboboxRef = useRef<HTMLDivElement>(null);
    const menuId = useId();

    const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [isFieldFocused, setIsFieldFocused] = useState<boolean>(false);

    const hasActiveOption = isMenuOpen && activeIndex !== undefined;
    const activeOptionId = hasActiveOption ? buildOptionId(menuId, activeIndex) : undefined;
    const currentPlaceholder = isMenuOpen ? placeholderWhileMenuOpen : placeholder;
    const hasSelectedOptions = selectedOptions.length > 0;
    const lastOptionIndex = options.length - 1;

    function isOptionSelected(option: CvtConcernOption) {
        return selectedOptions.some((selectedOption) => selectedOption.value === option.value);
    }

    function removeSelectedOption(option: CvtConcernOption) {
        onChange(selectedOptions.filter((selectedOption) => selectedOption.value !== option.value));
    }

    function toggleOption(option: CvtConcernOption) {
        if (isOptionSelected(option)) {
            removeSelectedOption(option);
            return;
        }

        onChange([...selectedOptions, option]);
    }

    function handleChipRemove(option: CvtConcernOption) {
        removeSelectedOption(option);
        comboboxRef.current?.focus();
    }

    function handleComboboxBlur() {
        setIsMenuOpen(false);
    }

    /**
     * Opens the menu, moves through it, or checks or unchecks the active option and closes the menu. While the menu is
     * open, Escape closes only the menu, so a second Escape closes the modal.
     */
    function handleComboboxKeyDown(event: KeyboardEvent<HTMLDivElement>) {
        switch (event.key) {
            case ' ':
            case 'Enter':
                event.preventDefault();

                // Keeps photoeditorsdk's global Enter handler from clicking the combobox, which would toggle the menu
                event.stopPropagation();

                if (!isMenuOpen) {
                    setIsMenuOpen(true);
                    return;
                }

                if (activeIndex === undefined) {
                    return;
                }

                toggleOption(options[activeIndex]);
                setIsMenuOpen(false);
                return;
            case 'ArrowDown':
                event.preventDefault();

                if (!isMenuOpen) {
                    setIsMenuOpen(true);
                    return;
                }

                if (activeIndex === undefined) {
                    setActiveIndex(0);
                    return;
                }

                setActiveIndex(Math.min(activeIndex + 1, lastOptionIndex));
                return;
            case 'ArrowUp':
                event.preventDefault();

                if (!isMenuOpen) {
                    setIsMenuOpen(true);
                    return;
                }

                if (activeIndex === undefined) {
                    setActiveIndex(lastOptionIndex);
                    return;
                }

                setActiveIndex(Math.max(activeIndex - 1, 0));
                return;
            case 'Backspace':
                if (!hasSelectedOptions) {
                    return;
                }

                event.preventDefault();
                removeSelectedOption(selectedOptions[selectedOptions.length - 1]);
                return;
            case 'Escape':
                if (!isMenuOpen) {
                    return;
                }

                event.preventDefault();
                setIsMenuOpen(false);
                return;
        }
    }

    function handleFieldBlur() {
        setIsFieldFocused(false);
    }

    /** Opens or closes the menu and focuses the combobox, since `handleMouseDown` stops a click from focusing it. */
    function handleFieldClick() {
        comboboxRef.current?.focus();
        setIsMenuOpen(!isMenuOpen);
    }

    function handleFieldFocus() {
        setIsFieldFocused(true);
    }

    /**
     * Keeps the combobox focused when the mouse is pressed anywhere in the multi-select. Otherwise the combobox loses
     * focus and closes the menu before the click lands.
     */
    function handleMouseDown(event: MouseEvent<HTMLDivElement>) {
        event.preventDefault();
    }

    /** Makes the row under the mouse the active one, so the mouse and the arrow keys move the same fill. */
    function handleOptionHover(option: CvtConcernOption) {
        setActiveIndex(options.findIndex((listedOption) => listedOption.value === option.value));
    }

    function handleOptionToggle(option: CvtConcernOption) {
        toggleOption(option);
        setIsMenuOpen(false);
    }

    return (
        <div className={classes.multiSelect} onMouseDown={handleMouseDown}>
            <div
                className={classNames(classes.field, { [classes.focusedField]: isFieldFocused })}
                onBlur={handleFieldBlur}
                onClick={handleFieldClick}
                onFocus={handleFieldFocus}
            >
                {selectedOptions.map((option) => (
                    <CvtConcernsMultiSelectChip key={option.value} onRemove={handleChipRemove} option={option} />
                ))}
                <div
                    aria-activedescendant={activeOptionId}
                    aria-controls={menuId}
                    aria-expanded={isMenuOpen}
                    aria-label={ariaLabel}
                    className={classes.combobox}
                    onBlur={handleComboboxBlur}
                    onKeyDown={handleComboboxKeyDown}
                    ref={comboboxRef}
                    role="combobox"
                    tabIndex={0}
                >
                    {!hasSelectedOptions && <Typography color="grayFive">{currentPlaceholder}</Typography>}
                </div>
            </div>
            <div
                aria-label={ariaLabel}
                aria-multiselectable
                className={classes.menu}
                hidden={!isMenuOpen}
                id={menuId}
                role="listbox"
            >
                {options.map((option, index) => (
                    <CvtConcernsMultiSelectOption
                        id={buildOptionId(menuId, index)}
                        isActive={index === activeIndex}
                        isSelected={isOptionSelected(option)}
                        key={option.value}
                        onHover={handleOptionHover}
                        onToggle={handleOptionToggle}
                        option={option}
                    />
                ))}
            </div>
        </div>
    );
}

/** Makes a unique id for each menu row, so the combobox can tell screen readers which row is active. */
function buildOptionId(menuId: string, index: number) {
    return `${menuId}-option-${index}`;
}
